<template>
  <div class="execution-progress-heatmap" v-loading="loading">
    <el-form :inline="true" size="small" class="filter-panel no-print" @submit.native.prevent>
      <el-row :gutter="12" class="filter-row">
        <el-col :span="6">
          <el-form-item label="统计年度">
            <el-date-picker
              v-model="filters.year"
              type="year"
              value-format="yyyy"
              placeholder="选择年"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="统计月份">
            <el-select v-model="filters.month" clearable placeholder="全年">
              <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="计划层级">
            <el-select v-model="filters.planType" placeholder="请选择">
              <el-option label="年计划" value="0" />
              <el-option label="月计划" value="1" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="客户">
            <el-select v-model="filters.custNo" clearable filterable placeholder="全部客户">
              <el-option
                v-for="item in ownerOptions"
                :key="item.id || item.deptId"
                :label="item.deptName || item.custName"
                :value="String(item.id || item.deptId)"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="物资">
            <el-select
              v-model="filters.cargoNo"
              clearable
              filterable
              remote
              :remote-method="loadCargoOptions"
              placeholder="全部物资"
            >
              <el-option
                v-for="item in cargoOptions"
                :key="item.varNo"
                :label="item.varName"
                :value="item.varNo"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="风险等级">
            <el-select v-model="filters.riskLevel" clearable placeholder="全部">
              <el-option label="低风险" :value="0" />
              <el-option label="中风险" :value="1" />
              <el-option label="高风险" :value="2" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12" class="filter-actions">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
          <el-button icon="el-icon-refresh" @click="loadData">刷新</el-button>
          <el-button type="primary" plain icon="el-icon-printer" :loading="exporting" @click="handleExportPdf">导出PDF</el-button>
        </el-col>
      </el-row>
    </el-form>

    <section class="kpi-grid">
      <div v-for="item in kpiList" :key="item.key" :class="['kpi-card', item.type]">
        <span>{{ item.label }}</span>
        <strong>{{ item.value }}</strong>
        <small>{{ item.extra }}</small>
      </div>
    </section>

    <section class="main-grid">
      <div class="panel heatmap-panel">
        <div class="panel-head">
          <span>客户月计划执行热力矩阵</span>
          <small>按月计划口径展示，色值按完成率和风险等级展示</small>
        </div>
        <div class="heatmap-body" v-loading="heatmapLoading">
          <div class="legend">
            <span><i class="heat-good"></i>90%以上</span>
            <span><i class="heat-ok"></i>70%-90%</span>
            <span><i class="heat-warn"></i>50%-70%</span>
            <span><i class="heat-risk"></i>高风险/低于50%</span>
            <span><i class="heat-empty"></i>无计划</span>
          </div>
          <div v-if="heatmapRows.length" class="heat-grid" :style="heatGridStyle">
            <div class="axis head-axis">客户 / 月份</div>
            <div v-for="month in heatmapMonths" :key="month.key" class="axis">{{ month.label }}</div>
            <template v-for="customer in heatmapRows">
              <div :key="customerKey(customer)" class="customer-name" :title="customer.custName">
                {{ customer.custName || '--' }}
              </div>
              <button
                v-for="month in heatmapMonths"
                :key="customerKey(customer) + '-' + month.key"
                type="button"
                :class="['heat-cell', heatCellClass(cellFor(customer, month))]"
                @click="handleHeatCellClick(cellFor(customer, month), customer)"
              >
                <b>{{ cellText(cellFor(customer, month)) }}</b>
                <span>{{ cellSubText(cellFor(customer, month)) }}</span>
              </button>
            </template>
          </div>
          <el-empty v-else description="暂无热力图数据" :image-size="90" />
        </div>
      </div>

      <div class="side-stack">
        <div class="panel">
          <div class="panel-head">
            <span>高风险客户排行</span>
            <el-tag size="mini" type="warning">汽运出库</el-tag>
          </div>
          <div class="risk-list">
            <div v-for="row in topRiskCustomers" :key="rankKey(row)" class="risk-card" @click="openCustomer(row)">
              <div class="risk-line">
                <b :title="row.custName">{{ row.custName || '--' }}</b>
                <span :class="toNumber(row.progressDeviation) >= 0 ? 'ok' : 'bad'">{{ signedPct(row.progressDeviation) }}</span>
              </div>
              <el-progress
                :percentage="riskProgress(row)"
                :stroke-width="8"
                :show-text="false"
                :color="riskColor(row.riskLevel)"
              />
              <p>{{ row.riskReasonText || '整体可控' }}</p>
            </div>
            <el-empty v-if="!topRiskCustomers.length" description="暂无风险客户" :image-size="70" />
          </div>
        </div>

        <div class="panel">
          <div class="panel-head"><span>关注事项分布</span></div>
          <div class="reason-list">
            <div v-for="item in riskReasons" :key="item.code || item.name" class="reason-row">
              <span>{{ item.name }}</span>
              <el-progress :percentage="reasonPercent(item.count)" :stroke-width="8" :show-text="false" />
              <b>{{ item.count || 0 }}</b>
            </div>
            <el-empty v-if="!riskReasons.length" description="暂无关注事项" :image-size="70" />
          </div>
        </div>
      </div>
    </section>

    <section class="sub-grid">
      <div class="panel">
        <div class="panel-head">
          <span>月度计划量 vs 执行量</span>
          <small>单位：吨</small>
        </div>
        <div ref="trendChart" class="chart-box"></div>
      </div>
      <div class="panel">
        <div class="panel-head"><span>风险结构</span></div>
        <div ref="riskChart" class="chart-box risk-chart"></div>
      </div>
    </section>

    <section class="panel detail-panel">
      <div class="panel-head">
        <span>计划明细</span>
        <small>保留计划追溯能力</small>
      </div>
      <div class="detail-body" v-loading="detailLoading">
        <el-table :data="detailRows" stripe size="small" class="progress-table" empty-text="暂无计划明细">
          <el-table-column prop="planName" label="计划名称" min-width="180" show-overflow-tooltip />
          <el-table-column prop="custName" label="客户" min-width="150" show-overflow-tooltip />
          <el-table-column prop="cargoName" label="物资" min-width="120" show-overflow-tooltip />
          <el-table-column prop="planTotalWeight" label="计划量(吨)" width="120" align="right" :formatter="tonCol" />
          <el-table-column prop="planExecuteWeight" label="执行量(吨)" width="120" align="right" :formatter="tonCol" />
          <el-table-column label="完成率" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
          </el-table-column>
          <el-table-column label="时间进度" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtPct(row.timeProgress) }}</template>
          </el-table-column>
          <el-table-column label="偏差" width="90" align="right">
            <template slot-scope="{ row }">
              <span :class="toNumber(row.progressDeviation) >= 0 ? 'ok' : 'bad'">{{ signedPct(row.progressDeviation) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="风险" width="92">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ row.riskLevelLabel || riskLabel(row.riskLevel) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="riskReasonText" label="关注事项" min-width="180" show-overflow-tooltip />
        </el-table>
        <div class="pager-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page.sync="filters.page"
            :page-size.sync="filters.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="detailTotal"
            @size-change="loadDetail"
            @current-change="loadDetail"
          />
        </div>
      </div>
    </section>

    <el-drawer
      :visible.sync="drawerVisible"
      size="560px"
      append-to-body
      destroy-on-close
      :with-header="false"
      custom-class="progress-detail-drawer"
      @closed="disposeDrawerChart"
    >
      <div class="drawer-head">
        <div>
          <h3>{{ customerDetail.custName || '客户详情' }}</h3>
          <p>计划完成趋势与关注事项</p>
        </div>
        <el-button icon="el-icon-close" circle size="small" @click="drawerVisible = false" />
      </div>
      <div class="drawer-body" v-loading="drawerLoading">
        <div class="drawer-stats">
          <div><span>计划量</span><b>{{ fmtTon(customerOverview.planTotalWeight) }} 吨</b></div>
          <div><span>已执行量</span><b>{{ fmtTon(customerOverview.planExecuteWeight) }} 吨</b></div>
          <div><span>完成率</span><b>{{ fmtPct(customerOverview.completeRate) }}</b></div>
          <div><span>进度偏差</span><b :class="toNumber(customerOverview.progressDeviation) >= 0 ? 'ok' : 'bad'">{{ signedPct(customerOverview.progressDeviation) }}</b></div>
        </div>
        <h4>客户趋势</h4>
        <div ref="drawerTrendChart" class="chart-box drawer-chart"></div>
        <h4>关注事项</h4>
        <div class="drawer-tags">
          <el-tag v-for="item in customerDetail.riskReasons || []" :key="item" size="small">{{ item }}</el-tag>
          <span v-if="!customerDetail.riskReasons || !customerDetail.riskReasons.length" class="empty-text">暂无关注事项</span>
        </div>
        <h4>计划明细</h4>
        <el-table :data="customerDetail.planRows || []" stripe size="mini" class="progress-table" max-height="240" empty-text="暂无计划明细">
          <el-table-column prop="planName" label="计划名称" min-width="160" show-overflow-tooltip />
          <el-table-column prop="cargoName" label="物资" width="110" show-overflow-tooltip />
          <el-table-column label="完成率" width="82" align="right">
            <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
          </el-table-column>
          <el-table-column label="风险" width="84">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ row.riskLevelLabel || riskLabel(row.riskLevel) }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  fetchOverview,
  fetchHeatmap,
  fetchMonthTrend,
  fetchRiskReasons,
  fetchCustomerRank,
  fetchDetailPage,
  fetchCustomerDetail,
  fetchOwnerOptions,
  fetchCargoOptions
} from './api'

const BUSINESS_TYPE_TRUCK_OUT = 2

export default {
  name: 'AutomobileExecutionProgressHeatmap',
  data() {
    return {
      loading: false,
      heatmapLoading: false,
      detailLoading: false,
      drawerLoading: false,
      exporting: false,
      filters: this.defaultFilters(),
      ownerOptions: [],
      cargoOptions: [],
      overview: {},
      heatmap: { xAxis: [], yAxis: [], cells: [] },
      monthTrend: [],
      riskReasons: [],
      customerRank: [],
      detailRows: [],
      detailTotal: 0,
      customerDetail: {},
      drawerVisible: false,
      charts: {},
      drawerChart: null
    }
  },
  computed: {
    heatmapMonths() {
      const axis = this.heatmap && this.heatmap.xAxis
      if (axis && axis.length) {
        return axis.map((item, index) => {
          if (typeof item === 'object') {
            return {
              key: item.month || item.key || index + 1,
              label: item.monthLabel || item.label || `${index + 1}月`
            }
          }
          return { key: index + 1, label: item }
        })
      }
      return Array.from({ length: 12 }).map((_, index) => ({ key: index + 1, label: `${index + 1}月` }))
    },
    heatmapRows() {
      return (this.heatmap && this.heatmap.yAxis) || []
    },
    heatGridStyle() {
      return {
        gridTemplateColumns: `160px repeat(${this.heatmapMonths.length || 12}, minmax(48px, 1fr))`
      }
    },
    customerOverview() {
      return (this.customerDetail && this.customerDetail.overview) || {}
    },
    topRiskCustomers() {
      return (this.customerRank || []).slice(0, 5)
    },
    kpiList() {
      return [
        { key: 'total', label: '计划总量', value: this.fmtTon(this.overview.planTotalWeight), extra: '吨', type: '' },
        { key: 'execute', label: '已执行量', value: this.fmtTon(this.overview.planExecuteWeight), extra: `完成率 ${this.fmtPct(this.overview.completeRate)}`, type: 'success' },
        { key: 'remain', label: '剩余计划量', value: this.fmtTon(this.overview.planRemainWeight), extra: '吨', type: '' },
        { key: 'time', label: '时间进度', value: this.fmtPct(this.overview.timeProgress), extra: '当前统计周期', type: 'warning' },
        { key: 'deviation', label: '进度偏差', value: this.signedPct(this.overview.progressDeviation), extra: '完成率 - 时间进度', type: this.toNumber(this.overview.progressDeviation) >= 0 ? 'success' : 'danger' },
        { key: 'risk', label: '高风险计划', value: this.formatInt(this.overview.highRiskPlanCount), extra: '超期或严重滞后', type: 'danger' }
      ]
    }
  },
  mounted() {
    this.loadOwnerOptions()
    this.loadCargoOptions('')
    this.loadData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
    this.disposeDrawerChart()
  },
  methods: {
    defaultFilters() {
      return {
        year: String(new Date().getFullYear()),
        month: null,
        planType: '1',
        custNo: null,
        cargoNo: null,
        riskLevel: null,
        page: 1,
        size: 20
      }
    },
    buildQuery(extra = {}) {
      return {
        ...this.filters,
        year: Number(this.filters.year) || new Date().getFullYear(),
        businessType: BUSINESS_TYPE_TRUCK_OUT,
        planType: this.filters.planType || '1',
        ...extra
      }
    },
    async loadData() {
      this.loading = true
      this.heatmapLoading = true
      try {
        const query = this.buildQuery({ page: undefined, size: 20 })
        const heatmapQuery = { ...query, planType: '1' }
        const [overview, heatmap, monthTrend, riskReasons, customerRank] = await Promise.all([
          fetchOverview(query),
          fetchHeatmap(heatmapQuery),
          fetchMonthTrend(query),
          fetchRiskReasons(query),
          fetchCustomerRank(query)
        ])
        this.overview = overview || {}
        this.heatmap = {
          xAxis: (heatmap && heatmap.xAxis) || [],
          yAxis: (heatmap && heatmap.yAxis) || [],
          cells: (heatmap && heatmap.cells) || []
        }
        this.monthTrend = monthTrend || []
        this.riskReasons = riskReasons || []
        this.customerRank = customerRank || []
        await this.loadDetail()
        this.$nextTick(this.renderCharts)
      } catch (e) {
        this.$message.error((e && e.message) || '执行进度数据加载失败')
      } finally {
        this.loading = false
        this.heatmapLoading = false
      }
    },
    async loadDetail() {
      this.detailLoading = true
      try {
        const pageData = await fetchDetailPage(this.buildQuery())
        this.detailRows = (pageData && pageData.records) || []
        this.detailTotal = Number((pageData && pageData.total) || 0)
      } catch (e) {
        this.detailRows = []
        this.detailTotal = 0
        this.$message.error((e && e.message) || '计划明细加载失败')
      } finally {
        this.detailLoading = false
      }
    },
    async loadOwnerOptions() {
      try {
        const data = await fetchOwnerOptions()
        this.ownerOptions = Array.isArray(data) ? data : []
      } catch (e) {
        this.ownerOptions = []
      }
    },
    async loadCargoOptions(keyword) {
      try {
        const data = await fetchCargoOptions(keyword || '')
        this.cargoOptions = Array.isArray(data) ? data : []
      } catch (e) {
        this.cargoOptions = []
      }
    },
    handleSearch() {
      this.filters.page = 1
      this.loadData()
    },
    handleReset() {
      this.filters = this.defaultFilters()
      this.loadData()
    },
    handleExportPdf() {
      this.exporting = true
      document.body.classList.add('execution-progress-printing')
      const clearPrintClass = () => {
        document.body.classList.remove('execution-progress-printing')
        window.removeEventListener('afterprint', clearPrintClass)
        this.exporting = false
      }
      window.addEventListener('afterprint', clearPrintClass)
      this.$nextTick(() => {
        window.print()
        setTimeout(clearPrintClass, 1000)
      })
    },
    cellFor(customer, month) {
      const custNo = customer && customer.custNo
      return ((this.heatmap && this.heatmap.cells) || []).find(cell => {
        const sameCustomer = String(cell.custNo || '') === String(custNo || '')
        const cellMonth = Number(cell.month || cell.monthKey)
        return sameCustomer && cellMonth === Number(month.key)
      })
    },
    handleHeatCellClick(cell, customer) {
      if (!cell && !customer) return
      this.openCustomer({
        ...(customer || {}),
        ...(cell || {}),
        custNo: (cell && cell.custNo) || (customer && customer.custNo),
        custName: (cell && cell.custName) || (customer && customer.custName)
      })
    },
    async openCustomer(row) {
      if (!row || !row.custNo) return
      this.drawerVisible = true
      this.drawerLoading = true
      try {
        const data = await fetchCustomerDetail(this.buildQuery({
          custNo: row.custNo,
          page: undefined,
          size: undefined
        }))
        this.customerDetail = data || {}
        this.$nextTick(this.renderDrawerChart)
      } catch (e) {
        this.customerDetail = {}
        this.$message.error((e && e.message) || '客户详情加载失败')
      } finally {
        this.drawerLoading = false
      }
    },
    renderCharts() {
      this.disposeCharts()
      this.renderTrendChart()
      this.renderRiskChart()
    },
    renderTrendChart() {
      const el = this.$refs.trendChart
      if (!el) return
      const chart = echarts.init(el)
      const rows = this.monthTrend || []
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 8, data: ['计划量', '执行量'] },
        grid: { left: 12, right: 18, top: 36, bottom: 48, containLabel: true },
        xAxis: { type: 'category', data: rows.map(item => item.monthLabel) },
        yAxis: { type: 'value', name: '吨' },
        series: [
          { name: '计划量', type: 'bar', data: rows.map(item => this.toNumber(item.planTotalWeight)), barMaxWidth: 34, itemStyle: { color: '#97a6ba', borderRadius: [4, 4, 0, 0] } },
          { name: '执行量', type: 'bar', data: rows.map(item => this.toNumber(item.planExecuteWeight)), barMaxWidth: 34, itemStyle: { color: '#1f6fd1', borderRadius: [4, 4, 0, 0] } }
        ]
      })
      this.charts.trend = chart
    },
    renderRiskChart() {
      const el = this.$refs.riskChart
      if (!el) return
      const chart = echarts.init(el)
      const data = [
        { name: '低风险', value: this.toNumber(this.overview.lowRiskPlanCount), itemStyle: { color: '#109461' } },
        { name: '中风险', value: this.toNumber(this.overview.mediumRiskPlanCount), itemStyle: { color: '#c47a0a' } },
        { name: '高风险', value: this.toNumber(this.overview.highRiskPlanCount), itemStyle: { color: '#c83a2c' } }
      ]
      chart.setOption({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0 },
        series: [{
          type: 'pie',
          radius: ['46%', '68%'],
          center: ['50%', '43%'],
          data
        }]
      })
      this.charts.risk = chart
    },
    renderDrawerChart() {
      this.disposeDrawerChart()
      const el = this.$refs.drawerTrendChart
      if (!el) return
      const trend = (this.customerDetail && this.customerDetail.monthTrend) || []
      const chart = echarts.init(el)
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: ['计划量', '执行量'] },
        grid: { left: 12, right: 16, top: 28, bottom: 45, containLabel: true },
        xAxis: { type: 'category', data: trend.map(item => item.monthLabel) },
        yAxis: { type: 'value', name: '吨' },
        series: [
          { name: '计划量', type: 'bar', data: trend.map(item => this.toNumber(item.planTotalWeight)), itemStyle: { color: '#97a6ba' } },
          { name: '执行量', type: 'line', smooth: true, data: trend.map(item => this.toNumber(item.planExecuteWeight)), itemStyle: { color: '#1f6fd1' } }
        ]
      })
      this.drawerChart = chart
    },
    disposeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) this.charts[key].dispose()
      })
      this.charts = {}
    },
    disposeDrawerChart() {
      if (this.drawerChart) {
        this.drawerChart.dispose()
        this.drawerChart = null
      }
    },
    resizeCharts() {
      this.$nextTick(() => {
        Object.keys(this.charts).forEach(key => this.charts[key] && this.charts[key].resize())
        if (this.drawerChart) this.drawerChart.resize()
      })
    },
    heatCellClass(cell) {
      if (!cell) return 'heat-empty'
      if (Number(cell.riskLevel) === 2 || this.toNumber(cell.completeRate) < 50) return 'heat-risk'
      if (this.toNumber(cell.completeRate) >= 90) return 'heat-good'
      if (this.toNumber(cell.completeRate) >= 70) return 'heat-ok'
      return 'heat-warn'
    },
    cellText(cell) {
      return cell ? this.fmtPct(cell.completeRate) : '-'
    },
    cellSubText(cell) {
      return cell ? `${this.fmtTon(cell.planExecuteWeight)}t` : '无计划'
    },
    riskProgress(row) {
      const deviation = Math.abs(this.toNumber(row.progressDeviation))
      return Math.max(8, Math.min(100, Math.round(deviation * 4)))
    },
    reasonPercent(count) {
      const max = Math.max(1, ...this.riskReasons.map(item => this.toNumber(item.count)))
      return Math.round(this.toNumber(count) / max * 100)
    },
    riskColor(level) {
      if (Number(level) === 2) return '#c83a2c'
      if (Number(level) === 1) return '#c47a0a'
      return '#109461'
    },
    riskTagType(level) {
      if (Number(level) === 2) return 'danger'
      if (Number(level) === 1) return 'warning'
      return 'success'
    },
    riskLabel(level) {
      if (Number(level) === 2) return '高风险'
      if (Number(level) === 1) return '中风险'
      return '低风险'
    },
    customerKey(customer) {
      return `customer-${customer && customer.custNo ? customer.custNo : customer && customer.custName}`
    },
    rankKey(row) {
      return `${row.custNo || row.custName}-${row.businessType || ''}-${row.planType || ''}`
    },
    tonCol(row, col, val) {
      return this.fmtTon(val)
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') return 0
      const n = Number(String(value).replace(/,/g, ''))
      return Number.isFinite(n) ? n : 0
    },
    fmtTon(value) {
      return this.toNumber(value).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fmtPct(value) {
      const n = this.toNumber(value)
      return `${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    },
    signedPct(value) {
      const n = this.toNumber(value)
      return `${n > 0 ? '+' : ''}${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    },
    formatInt(value) {
      return String(Math.round(this.toNumber(value)))
    }
  }
}
</script>

<style scoped lang="scss">
.execution-progress-heatmap {
  min-height: 100%;
  color: #1f2937;
  background: #eef3f8;

  .filter-panel,
  .panel,
  .kpi-card {
    background: #fff;
    border: 1px solid #dfe7f1;
    border-radius: 6px;
    box-shadow: none;
  }

  .filter-panel {
    padding: 12px 16px 10px;
    margin-bottom: 10px;

    .filter-row {
      width: 100%;
      margin: 0 !important;
      padding: 10px 5px 3px;
      background: #f3f8ff;
      box-sizing: border-box;
    }

    ::v-deep .el-col {
      margin-bottom: 7px;
    }

    ::v-deep .el-form-item {
      display: flex;
      margin-bottom: 0;
      margin-right: 0;
      min-width: 0;
    }

    ::v-deep .el-form-item__label {
      flex: 0 0 78px;
      padding-right: 10px;
      box-sizing: border-box;
    }

    ::v-deep .el-form-item__content {
      flex: 1;
      min-width: 0;
    }

    ::v-deep .el-select,
    ::v-deep .el-date-editor {
      width: 100% !important;
    }
  }

  .filter-actions {
    padding-top: 4px;
    text-align: right;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
    margin-bottom: 10px;
  }

  .kpi-card {
    min-height: 88px;
    padding: 13px 14px;
    box-sizing: border-box;

    span {
      color: #64748b;
      font-size: 12px;
    }

    strong {
      display: block;
      margin-top: 8px;
      color: #111827;
      font-size: 22px;
      line-height: 1.15;
      font-weight: 700;
    }

    small {
      display: block;
      margin-top: 7px;
      color: #94a3b8;
      font-size: 12px;
    }

    &.success strong {
      color: #0f8f5b;
    }

    &.warning strong {
      color: #b76b00;
    }

    &.danger strong {
      color: #c53126;
    }
  }

  .main-grid {
    display: grid;
    grid-template-columns: minmax(760px, 1.45fr) minmax(360px, 0.75fr);
    gap: 10px;
    margin-bottom: 10px;
  }

  .side-stack {
    display: grid;
    gap: 10px;
  }

  .panel {
    overflow: hidden;
  }

  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 43px;
    padding: 0 14px;
    border-bottom: 1px solid #e5edf6;
    font-weight: 600;
    box-sizing: border-box;

    small {
      color: #8a97a8;
      font-weight: 400;
    }
  }

  .heatmap-body {
    padding: 12px 14px 14px;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    color: #667085;
    font-size: 12px;

    i {
      display: inline-block;
      width: 14px;
      height: 14px;
      margin-right: 4px;
      border-radius: 3px;
      vertical-align: -2px;
    }
  }

  .heat-grid {
    display: grid;
    gap: 6px;
    align-items: stretch;
    overflow-x: auto;
  }

  .axis,
  .customer-name {
    min-height: 34px;
    padding: 0 8px;
    border-radius: 4px;
    background: #f8fafc;
    color: #667085;
    line-height: 34px;
    font-size: 12px;
  }

  .customer-name {
    color: #344054;
    font-weight: 600;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .heat-cell {
    min-height: 44px;
    padding: 6px 5px;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #fff;
    text-align: center;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px rgba(15, 23, 42, 0.14);
    }

    b {
      display: block;
      font-size: 13px;
      line-height: 16px;
    }

    span {
      display: block;
      margin-top: 3px;
      font-size: 11px;
      opacity: 0.88;
    }
  }

  .heat-empty {
    background: #f1f5f9;
    color: #98a2b3;
    cursor: default;
  }

  .heat-good {
    background: #109461;
  }

  .heat-ok {
    background: #2375d1;
  }

  .heat-warn {
    background: #c47a0a;
  }

  .heat-risk {
    background: #c83a2c;
  }

  .hint {
    margin-top: 10px;
    color: #98a2b3;
    font-size: 12px;
  }

  .risk-list {
    padding: 12px 14px;
    display: grid;
    gap: 10px;
  }

  .risk-card {
    padding: 11px 12px;
    border: 1px solid #e5edf6;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;

    p {
      margin: 7px 0 0;
      color: #667085;
      font-size: 12px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  .risk-line {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 9px;

    b {
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  .reason-list {
    padding: 14px;
    display: grid;
    gap: 12px;
  }

  .reason-row {
    display: grid;
    grid-template-columns: 112px 1fr 36px;
    gap: 10px;
    align-items: center;
    color: #667085;
    font-size: 13px;
  }

  .sub-grid {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 10px;
    margin-bottom: 10px;
  }

  .chart-box {
    width: 100%;
    height: 270px;
    padding: 10px;
    box-sizing: border-box;
  }

  .risk-chart {
    height: 270px;
  }

  .detail-body {
    padding: 12px 14px 10px;
  }

  .pager-wrap {
    display: flex;
    justify-content: flex-end;
    padding-top: 10px;
  }

  .progress-table {
    width: 100%;
  }

  .progress-table ::v-deep th.el-table__cell {
    padding: 0;
    background: #f6f8fb;
    color: #344054;
    font-weight: 600;
    border-right: none;
  }

  .progress-table ::v-deep td.el-table__cell {
    padding: 8px 0;
    border-right: none;
  }

  .progress-table::before,
  .progress-table::after {
    display: none;
  }

  .ok {
    color: #0f8f5b;
    font-weight: 600;
  }

  .bad {
    color: #c53126;
    font-weight: 600;
  }

  .empty-text {
    color: #98a2b3;
    font-size: 12px;
  }
}

::v-deep .progress-detail-drawer {
  .drawer-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px 18px;
    border-bottom: 1px solid #e5edf6;

    h3 {
      margin: 0;
      color: #111827;
      font-size: 16px;
      font-weight: 600;
    }

    p {
      margin: 6px 0 0;
      color: #667085;
      font-size: 12px;
    }
  }

  .drawer-body {
    padding: 16px 18px 24px;

    h4 {
      margin: 18px 0 10px;
      color: #1f2937;
      font-size: 14px;
      font-weight: 600;
    }
  }

  .drawer-stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 9px;

    div {
      min-height: 64px;
      padding: 10px;
      border: 1px solid #e5edf6;
      border-radius: 6px;
      background: #fbfdff;
    }

    span {
      color: #667085;
      font-size: 12px;
    }

    b {
      display: block;
      margin-top: 6px;
      color: #111827;
    }
  }

  .drawer-chart {
    height: 230px;
  }

  .drawer-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

@media (max-width: 1200px) {
  .execution-progress-heatmap {
    .kpi-grid {
      grid-template-columns: repeat(3, 1fr);
    }

    .main-grid,
    .sub-grid {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 720px) {
  .execution-progress-heatmap {
    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>

<style lang="scss">
@media print {
  body.execution-progress-printing {
    background: #fff !important;
  }

  body.execution-progress-printing .avue-header,
  body.execution-progress-printing .avue-left,
  body.execution-progress-printing .avue-tags,
  body.execution-progress-printing .avue-shade,
  body.execution-progress-printing .no-print,
  body.execution-progress-printing .pager-wrap,
  body.execution-progress-printing .el-drawer__wrapper,
  body.execution-progress-printing .v-modal {
    display: none !important;
  }

  body.execution-progress-printing .avue-contail,
  body.execution-progress-printing .avue-layout,
  body.execution-progress-printing .avue-main,
  body.execution-progress-printing .view-container,
  body.execution-progress-printing .avue-view {
    padding: 0 !important;
    margin: 0 !important;
    background: #fff !important;
  }

  body.execution-progress-printing .execution-progress-heatmap {
    padding: 0 !important;
    min-width: 0 !important;
    background: #fff !important;
  }

  body.execution-progress-printing .main-grid,
  body.execution-progress-printing .sub-grid {
    display: block !important;
  }

  body.execution-progress-printing .panel,
  body.execution-progress-printing .kpi-card {
    break-inside: avoid;
    page-break-inside: avoid;
    box-shadow: none !important;
  }
}
</style>
